import React, { useState, useEffect } from "react";
import shotMatchCentreCheckin from "@/assets/docs/match-centre-checkin.webp";
import shotMatchCentreBoard from "@/assets/docs/match-centre-board.webp";
import shotDrawsGroups from "@/assets/docs/draws-groups.webp";
import shotAmericano from "@/assets/docs/format-americano.webp";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import { config } from "@/config/config.ts";
import LandingNav from "@/landing/components/LandingNav.tsx";
import FooterSection from "@/landing/sections/FooterSection.tsx";
import { createFileRoute } from '@tanstack/react-router'

// ── Nav structure ────────────────────────────────────────────────
const NAV = [
  { group: "Getting Started", items: [
    { id: "home", icon: "🏠", label: "What is ScorePal?" },
  ]},
  { group: "Profiles", items: [
    { id: "player", icon: "👤", label: "Player Profile" },
    { id: "playerdashboard", icon: "📈", label: "Player Dashboard" },
    { id: "community", icon: "🏘️", label: "Community Profile" },
    { id: "club", icon: "🏟️", label: "Club Profile" },
  ]},
  { group: "Tournaments", items: [
    { id: "tournament", icon: "🏆", label: "Create a Tournament" },
    { id: "tournoptions", icon: "📐", label: "Tournament Options" },
    { id: "clashofteams", icon: "⚔️", label: "Clash of Teams" },
    { id: "rydercup", icon: "🏌️", label: "Ryder Cup Tournament" },
    { id: "formats", icon: "🎲", label: "More Formats" },
    { id: "matchcentre", icon: "🎛️", label: "Match Centre" },
  ]},
  { group: "Sports", items: [
    { id: "teamsports", icon: "⚽", label: "Team Sports" },
  ]},
  { group: "Leagues", items: [
    { id: "league", icon: "📋", label: "Create a League" },
    { id: "leagueoptions", icon: "⚙️", label: "League Options" },
    { id: "ladder", icon: "🪜", label: "Ladder League" },
    { id: "division", icon: "🏛️", label: "Division Season" },
  ]},
  { group: "Leaderboards", items: [
    { id: "leagueofleagues", icon: "🌍", label: "League of Leagues" },
    { id: "leagueofplayers", icon: "🏅", label: "League of Players" },
    { id: "clubleaderboard", icon: "🥇", label: "Club Leaderboard" },
  ]},
  { group: "Match Making", items: [
    { id: "match", icon: "🎾", label: "Create a Match" },
    { id: "invite", icon: "✉️", label: "Invite Players" },
    { id: "referrals", icon: "💰", label: "Referral Program" },
  ]},
  { group: "Account", items: [
    { id: "wallet", icon: "💳", label: "Wallet" },
    { id: "payments", icon: "💸", label: "Payments" },
    { id: "courtbookings", icon: "🏟️", label: "Court Bookings" },
  ]},
  { group: "Ratings", items: [
    { id: "sppr", icon: "📊", label: "SPPR Rating" },
    { id: "elo", icon: "📈", label: "ELO Rating" },
    { id: "reliability", icon: "🎯", label: "Reliability Score" },
  ]},
];

const ALL_ITEMS = NAV.flatMap(g => g.items);

// ── Shared components ────────────────────────────────────────────
function Hero({ tag, title, children }: { tag: string; title: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: "linear-gradient(135deg,#051d5c 0%,#0d3fad 100%)",
      borderRadius: 16, padding: "44px 44px 36px", color: "#fff",
      marginBottom: 36, position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position:"absolute", bottom:-50, right:-30, width:240, height:240,
        background:"rgba(59,130,246,.09)", borderRadius:"50%",
      }}/>
      <span style={{
        display:"inline-block", background:"rgba(255,255,255,.12)",
        border:"1px solid rgba(255,255,255,.18)", color:"rgba(255,255,255,.85)",
        fontSize:11, fontWeight:600, letterSpacing:".08em", textTransform:"uppercase",
        padding:"3px 12px", borderRadius:20, marginBottom:14,
      }}>{tag}</span>
      <h1 style={{
        fontFamily:"'Bebas Neue',cursive", fontSize:48, fontWeight:400,
        lineHeight:1.1, marginBottom:10, letterSpacing:2, color:"#fff",
      }}>{title}</h1>
      <p style={{ color:"rgba(255,255,255,.68)", fontSize:15.5, maxWidth:520, lineHeight:1.65, marginBottom:0 }}>
        {children}
      </p>
    </div>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{
      fontFamily:"'Bebas Neue',cursive", fontSize:28, fontWeight:400,
      color:"#051d5c", margin:"36px 0 10px", letterSpacing:1,
    }}>{children}</h2>
  );
}

function H3({ children }: { children: React.ReactNode }) {
  return (
    <h3 style={{
      fontFamily:"'Bebas Neue',cursive", fontSize:20, fontWeight:400,
      color:"#0d3fad", margin:"26px 0 8px", letterSpacing:.5,
    }}>{children}</h3>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p style={{ color:"#334155", marginBottom:12, lineHeight:1.75 }}>{children}</p>;
}

function HR() {
  return <hr style={{ border:"none", borderTop:"1px solid #dde5f7", margin:"28px 0" }}/>;
}

function Callout({ type = "info", icon, children }: { type?: string; icon: string; children: React.ReactNode }) {
  const styles: Record<string, { bg: string; border: string; color: string }> = {
    info:    { bg:"#eff6ff", border:"#3B82F6", color:"#1e40af" },
    tip:     { bg:"#f0fdf4", border:"#10B981", color:"#166534" },
    warning: { bg:"#fffbeb", border:"#F59E0B", color:"#92400e" },
    pro:     { bg:"#faf5ff", border:"#8b5cf6", color:"#5b21b6" },
  };
  const s = styles[type] || styles.info;
  return (
    <div style={{
      background:s.bg, borderLeft:`3px solid ${s.border}`, borderRadius:10,
      padding:"14px 18px", margin:"18px 0", display:"flex", gap:11,
      fontSize:13.5, lineHeight:1.6, color:s.color,
    }}>
      <span style={{ fontSize:17, flexShrink:0, marginTop:1 }}>{icon}</span>
      <div>{children}</div>
    </div>
  );
}

/** A screenshot of the real screen, with the caption doing the explaining the picture can't. */
function Shot({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <figure style={{ margin:"22px 0" }}>
      <img src={src} alt={alt} loading="lazy" style={{
        width:"100%", borderRadius:12, border:"1px solid #e2e8f0",
        boxShadow:"0 6px 22px rgba(5,29,92,.10)", display:"block",
      }}/>
      <figcaption style={{ fontSize:12.5, color:"#64748b", marginTop:9, lineHeight:1.55 }}>{caption}</figcaption>
    </figure>
  );
}

function CardGrid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(210px,1fr))",
      gap:13, margin:"18px 0",
    }}>{children}</div>
  );
}

function Card({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return (
    <div style={{
      background:"#fff", border:"1px solid #dde5f7", borderRadius:12,
      padding:18,
    }}>
      <div style={{ fontSize:22, marginBottom:9 }}>{icon}</div>
      <h4 style={{ fontSize:13.5, fontWeight:600, margin:"0 0 5px", color:"#051d5c" }}>{title}</h4>
      <p style={{ fontSize:12.5, color:"#64748b", margin:0, lineHeight:1.5 }}>{children}</p>
    </div>
  );
}

function Steps({ steps }: { steps: { num?: string | number; title: string; body: string }[] }) {
  return (
    <div style={{ margin:"18px 0 24px" }}>
      {steps.map((s, i) => (
        <div key={i} style={{ display:"flex", gap:16, marginBottom:18, alignItems:"flex-start" }}>
          <div style={{
            flexShrink:0, width:30, height:30, background:"#082F8A", color:"#fff",
            borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
            fontFamily:"'Bebas Neue',cursive", fontWeight:700, fontSize:12, marginTop:3,
          }}>{s.num || i+1}</div>
          <div>
            <h4 style={{ margin:"0 0 3px", fontSize:14.5, fontWeight:600, color:"#051d5c" }}>{s.title}</h4>
            <p style={{ margin:0, fontSize:13.5, color:"#475569", lineHeight:1.6 }}>{s.body}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <table style={{ width:"100%", borderCollapse:"collapse", margin:"16px 0", fontSize:13.5, borderRadius:10, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,.05)" }}>
      <thead style={{ background:"#082F8A", color:"#fff" }}>
        <tr>{headers.map((h, i) => (
          <th key={i} style={{ padding:"11px 15px", textAlign:"left", fontFamily:"'Bebas Neue',cursive", fontSize:12, letterSpacing:".05em", textTransform:"uppercase", fontWeight:600 }}>{h}</th>
        ))}</tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} style={{ background: i%2===0 ? "#fff" : "#f8faff" }}>
            {row.map((cell, j) => (
              <td key={j} style={{ padding:"10px 15px", borderBottom:"1px solid #dde5f7", color:"#334155" }} dangerouslySetInnerHTML={{__html: cell}}/>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MetricRow({ metrics }: { metrics: { val: string; label: string }[] }) {
  return (
    <div style={{ display:"grid", gridTemplateColumns:`repeat(${metrics.length},1fr)`, gap:12, margin:"16px 0" }}>
      {metrics.map((m, i) => (
        <div key={i} style={{ background:"#fff", border:"1px solid #dde5f7", borderRadius:10, padding:16, textAlign:"center" }}>
          <div style={{ fontFamily:"'Bebas Neue',cursive", fontSize:30, fontWeight:400, color:"#082F8A", letterSpacing:1 }}>{m.val}</div>
          <div style={{ fontSize:12, color:"#64748b", marginTop:3 }}>{m.label}</div>
        </div>
      ))}
    </div>
  );
}

function FormulaBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background:"#051d5c", color:"#e2e8f0", borderRadius:10,
      padding:"18px 22px", fontFamily:"monospace", fontSize:14,
      margin:"16px 0", lineHeight:1.7,
    }}>{children}</div>
  );
}

// ── Section components ───────────────────────────────────────────
function SectionHome() {
  return (
    <div>
      <H2>What You'll Find Here</H2>
      <CardGrid>
        <Card icon="🚀" title="Getting Started Guides">Set up ScorePal quickly and confidently from your first login.</Card>
        <Card icon="⚙️" title="Configuration & Settings">Customise ScorePal to fit your event, club, and community.</Card>
        <Card icon="📊" title="Features & Best Practices">Make the most of scoring, performance tools, and analytics.</Card>
        <Card icon="🛠️" title="Troubleshooting & FAQs">Quick solutions to common questions and issues.</Card>
      </CardGrid>
      <H2>Who This Is For</H2>
      <Table
        headers={["User Type","Primary Use","Start Here"]}
        rows={[
          ["<strong>Organisers</strong>","Create leagues, tournaments, and manage events","Create a Tournament / League"],
          ["<strong>Players</strong>","Join competitions, track stats, find matches","Player Profile"],
          ["<strong>Club Admins</strong>","Manage community presence and members","Community Profile"],
        ]}
      />
      <Callout type="tip" icon="💡">If you're new, start with your <strong>Player Profile</strong> to understand your dashboard, then explore creating a league or tournament when you're ready.</Callout>
    </div>
  );
}

function SectionPlayer() {
  return (
    <div>
      <Hero tag="Profiles" title="Player Profile">Your Player Dashboard gives you a complete snapshot of your profile, matches, performance, and statistics — all in one place.</Hero>
      <H2>Quick Actions Bar</H2>
      <P>Two prominent buttons give you instant access to the most-used features:</P>
      <CardGrid>
        <Card icon="🏟️" title="Book a Court">Opens the court booking system to reserve a court at your club directly from your dashboard.</Card>
        <Card icon="🔍" title="Find a Game">Takes you to Matchmaking to browse and join available matches near you.</Card>
      </CardGrid>
      <P>Your <strong>leaderboard rank, current streak, total points, and tier</strong> are shown in a summary bar just below the header (e.g. #18 Global · Streak: 0 · 895 Points · Silver Tier).</P>
      <HR/>
      <H2>Dashboard Sections</H2>
      <H3>1 — Welcome Header</H3>
      <CardGrid>
        <Card icon="👋" title="What it shows">Your name, today's date, and quick-access icons for settings, notifications, and preferences.</Card>
        <Card icon="⚙️" title="What it does">Confirms you're logged in and provides quick access to account settings.</Card>
      </CardGrid>
      <H3>2 — Player Profile Card</H3>
      <CardGrid>
        <Card icon="🪪" title="What it shows">Profile image, player name, location, member since date.</Card>
        <Card icon="🌍" title="What it does">Displays your public-facing identity and registered location.</Card>
      </CardGrid>
      <Callout type="info" icon="ℹ️">This is the <strong>identity section</strong> of your profile — keep it up to date so opponents and organisers can recognise you.</Callout>
      <H3>3 — My Latest Match</H3>
      <CardGrid>
        <Card icon="📅" title="What it shows">Player cards, match date/time, location, competition name, and an Add to Calendar button.</Card>
        <Card icon="🔗" title="What it does">Reminds you of your latest fixture and lets you add it to your calendar.</Card>
      </CardGrid>
      <H3>4 — Latest Scores</H3>
      <CardGrid>
        <Card icon="🏅" title="What it shows">Singles/Doubles toggle, recent results with WIN/LOSE indicators, match scores and dates.</Card>
        <Card icon="📈" title="What it does">Displays recent results and lets you filter by match type.</Card>
      </CardGrid>
      <H3>5 — Leaderboard</H3>
      <CardGrid>
        <Card icon="🌍" title="What it shows">Your global rank (e.g. #18 Global), top 3 players with points, your own points, and a View Full Leaderboard link.</Card>
        <Card icon="🎯" title="What it does">Lets you instantly see where you stand globally without leaving your dashboard.</Card>
      </CardGrid>
      <H3>6 — AI Insights</H3>
      <Callout type="pro" icon="⭐">Paid Feature — Racquet Sports Only. Available on the Pro Player plan.</Callout>
      <CardGrid>
        <Card icon="🤖" title="What it shows">An AI Insights panel with a View Insights button.</Card>
        <Card icon="💡" title="What it does">Provides strength/weakness analysis, performance trends, and improvement suggestions.</Card>
      </CardGrid>
      <H3>7 — Performance Metrics</H3>
      <MetricRow metrics={[{val:"100%",label:"Reliability"},{val:"4.75",label:"SPPR Rating"},{val:"1280",label:"ELO Rating"}]}/>
      <Table
        headers={["Metric","What It Measures"]}
        rows={[
          ["<strong>Reliability</strong>","Match participation and consistency"],
          ["<strong>SPPR</strong>","ScorePal Performance Rating"],
          ["<strong>ELO Rating</strong>","Competitive ranking score adjusting dynamically after each match"],
        ]}
      />
      <H3>8 — Career Statistics</H3>
      <MetricRow metrics={[{val:"37",label:"Games Played"},{val:"22",label:"Games Won"},{val:"15",label:"Games Lost"}]}/>
      <HR/>
      <H2>How to Use Your Dashboard Effectively</H2>
      <Steps steps={[
        {title:"Review your Latest Match",body:"Stay updated on competitions and upcoming fixtures."},
        {title:"Track trends in Latest Scores",body:"Toggle between Singles and Doubles to monitor your recent form."},
        {title:"Monitor your ratings",body:"Watch ELO and SPPR over time to understand your competitive progress."},
        {title:"Use AI Insights",body:"Review personalised recommendations to identify areas for improvement (Pro plan)."},
        {title:"Keep your profile updated",body:"Accurate location and profile info helps with matchmaking and discoverability."},
      ]}/>
    </div>
  );
}

function SectionPlayerDashboard() {
  return (
    <div>
      <Hero tag="Profiles" title="Player Dashboard">A deep-dive analytics view of your playing career — ratings, match history, tournament performance, win streaks, best partner, recent form, and a full rating chart over time.</Hero>
      <P>Access via your player profile. Use the <strong>Pair</strong> and <strong>PvP</strong> toggles to switch between doubles pair stats and head-to-head individual stats. The <strong>Date Range</strong> picker filters all metrics to a custom time window.</P>
      <H2>Summary Cards</H2>
      <Table
        headers={["Card","What It Shows"]}
        rows={[
          ["<strong>Global Rating</strong>","Your current ELO rating (e.g. 1264) with your tier badge"],
          ["<strong>Played Matches</strong>","Total matches with Won/Lost breakdown (e.g. 43 played — 25 Won, 18 Lost)"],
          ["<strong>Win %</strong>","Overall win percentage in the selected date range (e.g. 58.14%)"],
          ["<strong>Tournaments Won</strong>","Total tournament titles won"],
        ]}
      />
      <H2>Recent Matches</H2>
      <Table
        headers={["Column","Description"]}
        rows={[
          ["<strong>Date</strong>","Date the match was played"],
          ["<strong>Game</strong>","League or tournament the match was part of"],
          ["<strong>Score</strong>","Final score of the match"],
          ["<strong>Result</strong>","W (Win) or L (Loss)"],
          ["<strong>ELO</strong>","ELO rating change from that match"],
          ["<strong>SPPR</strong>","SPPR rating change from that match"],
        ]}
      />
      <H2>Win Streaks</H2>
      <CardGrid>
        <Card icon="🔥" title="Longest Win Streak">Highest number of consecutive wins achieved.</Card>
        <Card icon="🟢" title="Current Streak">Your active win streak — resets after a loss.</Card>
        <Card icon="📉" title="Last 10 Win Rate">Win percentage across your most recent 10 matches (e.g. 40%).</Card>
        <Card icon="🕐" title="Weekly Average Matches">Average matches per week over the selected period (e.g. 0.6).</Card>
      </CardGrid>
      <H2>Tournament Performance</H2>
      <CardGrid>
        <Card icon="🏆" title="Tournaments">Total entered, broken down into Won and Finals reached.</Card>
        <Card icon="🏆" title="Group Stage Qualifications">% of tournaments where you qualified from the group stage (e.g. 100%).</Card>
        <Card icon="🥇" title="1st & 2nd Place Finishes">Total gold and silver finishes across all tournaments.</Card>
        <Card icon="⭐" title="Top Tournament Performance">Your best single tournament result highlighted.</Card>
      </CardGrid>
      <H2>Player Rating Chart</H2>
      <P>A line chart showing how your ELO rating has changed over time. Hover to see your rating at any specific point.</P>
      <Callout type="info" icon="ℹ️">Rating peaks align with winning streaks or strong tournament runs. Dips reflect losses or facing much stronger opponents. Use this chart to spot trends in your competitive development.</Callout>
      <H2>Additional Insights</H2>
      <CardGrid>
        <Card icon="🤝" title="Best Partner">The doubles partner you have the best win record with.</Card>
        <Card icon="📈" title="Recent Form">Your last 10 results as a colour-coded sequence — L for loss, W for win.</Card>
        <Card icon="🏆" title="Leagues Won">Total league titles won across all competitions.</Card>
        <Card icon="📊" title="Overall Win Rate">Career win percentage across all matches in the selected date range.</Card>
      </CardGrid>
      <HR/>
      <H2>Pair Rankings</H2>
      <P>Switch to the <strong>Pair</strong> tab to see a full ranking of every doubles partnership you have played in, ordered by matches played.</P>
      <Table
        headers={["Column","Description"]}
        rows={[
          ["<strong>Rank</strong>","Position ordered by most matches played together"],
          ["<strong>Partnership</strong>","Your name paired with each partner"],
          ["<strong>Played</strong>","Total matches played together"],
          ["<strong>Wins</strong>","Total wins as that pair"],
          ["<strong>Win %</strong>","Win percentage for that specific partnership"],
        ]}
      />
      <HR/>
      <H2>Player vs Player Rankings</H2>
      <P>Click <strong>Player vs Player</strong> to see your head-to-head record against every opponent, ranked by win percentage.</P>
      <CardGrid>
        <Card icon="🥇" title="100% Win Rate">Opponents you have never lost to — consistent dominance.</Card>
        <Card icon="🥈" title="75% Win Rate">Opponents you usually beat but occasionally lose to.</Card>
        <Card icon="⚡" title="50% Win Rate">Evenly matched opponents — your most competitive rivalries.</Card>
        <Card icon="🔴" title="Under 50%">Opponents who have the edge — key improvement targets.</Card>
      </CardGrid>
      <Callout type="tip" icon="💡">Focus on opponents where you have played 4+ times but your win rate is below 50% — these are your key improvement targets.</Callout>
    </div>
  );
}

function SectionCommunity() {
  return (
    <div>
      <Hero tag="Profiles" title="Community Profile">The Community Profile Dashboard serves as the public-facing identity of your club — a central hub for competitions, events, engagement, and feedback.</Hero>
      <H2>Summary Bar</H2>
      <CardGrid>
        <Card icon="📊" title="Active Leagues">Number of currently running leagues in your community.</Card>
        <Card icon="⭐" title="Best Score">Your community's current best League of Leagues score.</Card>
        <Card icon="🌍" title="View League of Leagues →">Direct link to your community's League of Leagues standings.</Card>
      </CardGrid>
      <H2>Dashboard Sections</H2>
      <H3>1 — Welcome Header</H3>
      <CardGrid>
        <Card icon="👋" title="Welcome Message">Personalised greeting with today's date.</Card>
        <Card icon="⚙️" title="Action Icons">Quick access to Settings, Notifications, and Display controls.</Card>
      </CardGrid>
      <H3>2 — Community Profile Card</H3>
      <CardGrid>
        <Card icon="🏷️" title="Logo & Branding">Community logo and establishment year.</Card>
        <Card icon="📍" title="Contact & Location">Country, phone, email, and website link.</Card>
      </CardGrid>
      <H3>3 — Key Stats</H3>
      <CardGrid>
        <Card icon="🏆" title="Upcoming Tournaments">Number of scheduled tournaments.</Card>
        <Card icon="🥇" title="Active Leagues">How many leagues are currently running.</Card>
        <Card icon="💳" title="Membership Price">Current membership fee (if set).</Card>
      </CardGrid>
      <H3>4 — Next Upcoming Event</H3>
      <P>Shows the next scheduled event name, date & time, competition type, and a registration link. If no events are scheduled, displays "No events scheduled."</P>
      <Callout type="tip" icon="💡">Always keep at least one upcoming event published — an empty panel reduces member engagement.</Callout>
      <H3>5 — Social Media Links</H3>
      <P>Icons for Facebook, Instagram, YouTube, and WhatsApp connect members to your external platforms.</P>
      <H3>6 — Reviews</H3>
      <CardGrid>
        <Card icon="⭐" title="Star Rating">Members can leave a star rating for the community.</Card>
        <Card icon="💬" title="Leave & View Reviews">Leave Review button and View More link.</Card>
      </CardGrid>
      <H3>7 — Community Statistics</H3>
      <MetricRow metrics={[{val:"159",label:"Community Players"},{val:"94",label:"Matches Played"},{val:"3",label:"Active Leagues"}]}/>
      <H3>8 — Your League Rankings</H3>
      <Table
        headers={["Column","Meaning"]}
        rows={[
          ["<strong>League Name</strong>","Full league name"],
          ["<strong>Matches / Players</strong>","Matches played and total players enrolled"],
          ["<strong>C.0 P.0 A.0</strong>","Competitiveness, Participation, and Activity scores for League of Leagues"],
          ["<strong>Points</strong>","Total League of Leagues coins earned by this league"],
        ]}
      />
    </div>
  );
}

function SectionClub() {
  return (
    <div>
      <Hero tag="Profiles" title="Club Profile">The Club Dashboard combines brand identity, pricing, event promotion, and operational metrics — giving administrators full visibility into performance and participation.</Hero>
      <H2>Dashboard Sections</H2>
      <H3>1 — Welcome Header</H3>
      <CardGrid>
        <Card icon="👋" title="Welcome Message">Personalised greeting with today's date.</Card>
        <Card icon="⚙️" title="Action Icons">Quick access to Theme, Messages, Billing, and Settings.</Card>
      </CardGrid>
      <H3>2 — Club Profile Card</H3>
      <CardGrid>
        <Card icon="🏷️" title="Logo & Branding">Club logo and visual identity.</Card>
        <Card icon="📋" title="Club Information">Name, location, year founded, phone, email, and website.</Card>
      </CardGrid>
      <H3>3 — Costs Section</H3>
      <Table
        headers={["Cost Card","Description","Example"]}
        rows={[
          ["<strong>Monthly Membership</strong>","Recurring membership fee","$99 / month"],
          ["<strong>Court Pricing – 90 mins</strong>","Cost to book a court","$90"],
          ["<strong>Merchandise</strong>","Optional retail pricing","$10"],
        ]}
      />
      <Callout type="tip" icon="💡">Use the <strong>Off-Peak / Peak Hours toggle</strong> to show members how pricing changes depending on time of booking.</Callout>
      <H3>4 — Next Upcoming Event</H3>
      <CardGrid>
        <Card icon="📅" title="What it shows">Event name, date & time, location, and an Enquire Now button.</Card>
        <Card icon="🎯" title="What it does">Promotes upcoming events and drives registrations from the club profile.</Card>
      </CardGrid>
      <H3>5 — Social Media Links</H3>
      <P>Facebook, Instagram, YouTube, and WhatsApp icons connect members to your external platforms.</P>
      <H3>6 — Reviews</H3>
      <CardGrid>
        <Card icon="⭐" title="Star Rating">Members can leave a star rating and written feedback.</Card>
        <Card icon="💬" title="Leave & View Reviews">Leave Review button and View More link.</Card>
      </CardGrid>
      <H3>7 — Key Performance Indicators</H3>
      <MetricRow metrics={[{val:"76%",label:"Court Availability"},{val:"72",label:"Matches Played"}]}/>
      <HR/>
      <H2>For Club Administrators</H2>
      <Steps steps={[
        {title:"Monitor membership pricing",body:"Review Monthly Membership and Court Pricing cards to ensure rates are current."},
        {title:"Adjust peak / off-peak rates",body:"Use the pricing toggle to reflect different rates and manage court demand."},
        {title:"Promote upcoming tournaments",body:"Keep the Next Upcoming Event panel populated at all times."},
        {title:"Track court utilisation",body:"Monitor Court Availability % to inform scheduling decisions."},
        {title:"Review player feedback",body:"Check the Reviews section regularly to understand member sentiment."},
        {title:"Monitor engagement metrics",body:"Use Matches Played to gauge platform activity levels."},
      ]}/>
    </div>
  );
}

function SectionTournament() {
  return (
    <div>
      <Hero tag="Tournaments" title="Create a Tournament">Set up a professional tournament in four simple steps — from format selection through to inviting players and publishing your draws.</Hero>
      <H2>Step-by-Step Guide</H2>
      <H3>Step 1 — Click 'Add New Tournament'</H3>
      <P>From your organiser dashboard, click <strong>Add New Tournament</strong> in the top right corner to open the tournament creation wizard.</P>
      <H3>Step 2 — Select Sport & Tournament Type</H3>
      <Steps steps={[
        {num:"A",title:"Choose a Sport",body:"Select from: Padel, Pickleball, Squash, Badminton, or Tennis."},
        {num:"B",title:"Choose a Tournament Type",body:"Groups + Knockout (round-robin into elimination bracket) or Knockout Only (single elimination). Click Next."},
        {num:"C",title:"Choose Participants Type",body:"Singles, Doubles, or Team-based."},
      ]}/>
      <H3>Step 3 — Configure Tournament</H3>
      <Table
        headers={["Field","Description","Required"]}
        rows={[
          ["Tournament Name","Public name of your event","<span style='background:#fef3c7;color:#92400e;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:600'>Required</span>"],
          ["Match Type","Match format (e.g. best of 3)","Optional"],
          ["Visibility","Public or Private (invite-only)","<span style='background:#fef3c7;color:#92400e;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:600'>Required</span>"],
          ["Venue","Where the tournament will be held","<span style='background:#fef3c7;color:#92400e;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:600'>Required</span>"],
          ["Entry Deadline","Final date players can register","<span style='background:#fef3c7;color:#92400e;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:600'>Required</span>"],
          ["Start & End Date","Defines tournament duration","<span style='background:#fef3c7;color:#92400e;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:600'>Required</span>"],
        ]}
      />
      <H3>Step 4 — Invite Players</H3>
      <Steps steps={[
        {title:"Check player counter",body:"See current invited vs. maximum capacity (e.g. 0/16 players)."},
        {title:"Search existing players",body:"Find registered users by name and send invitations."},
        {title:"Create & Add a new player",body:"Manually add a player who doesn't have an account yet."},
        {title:"Skip if needed",body:"You can invite players later from the tournament management screen."},
      ]}/>
      <Callout type="warning" icon="⚠️">Players must <strong>accept their invitations</strong> before they become official participants in the draw.</Callout>
    </div>
  );
}

function SectionTournOptions() {
  return (
    <div>
      <Hero tag="Tournaments" title="Tournament Options">ScorePal supports two core tournament formats — Round Robin and Knockout. Understanding how each works helps you choose the right structure.</Hero>
      <H2>Round Robin</H2>
      <P>Every player plays against every other player in the group. No one is eliminated after a loss — rankings are based on overall performance.</P>
      <Callout type="tip" icon="💡">Ideal for leagues, group stages, and club competitions where you want everyone to get the same number of matches.</Callout>
      <Steps steps={[
        {title:"Every participant plays everyone else",body:"Each player faces all others once (single) or twice (double round robin)."},
        {title:"Points are accumulated",body:"Rankings determined by points or match results across all games played."},
        {title:"Tie-breakers resolve equal points",body:"Applied in order: Goal/Game difference → Head-to-head → Total games won → Fewest games lost."},
      ]}/>
      <CardGrid>
        <Card icon="1️⃣" title="Single Round Robin">Each team plays every other team once. Standard for most competitions.</Card>
        <Card icon="2️⃣" title="Double Round Robin">Each team plays every other team twice. Used in professional leagues for fairer results.</Card>
      </CardGrid>
      <HR/>
      <H2>Knockout Tournament</H2>
      <P>A player or team is <strong>eliminated immediately after losing</strong>. Winners advance until only one champion remains.</P>
      <Callout type="info" icon="ℹ️">Best for fast-paced, time-limited events or larger draws.</Callout>
      <Table
        headers={["Round","Players Remaining","Matches Played"]}
        rows={[
          ["Round of 16","16","8 matches"],
          ["Quarterfinals","8","4 matches"],
          ["Semifinals","4","2 matches"],
          ["Final","2","1 match"],
        ]}
      />
      <HR/>
      <H2>Which Format Should I Choose?</H2>
      <Table
        headers={["Format","Best For","Eliminations?","Matches per Player"]}
        rows={[
          ["<strong>Round Robin</strong>","Group stages, club leagues, fair play events","No","High — plays everyone"],
          ["<strong>Knockout</strong>","Large draws, one-day events","Yes — lose once and out","Low — depends on wins"],
          ["<strong>Groups + Knockout</strong>","Balanced tournaments","After group stage","Medium — groups + bracket"],
        ]}
      />
    </div>
  );
}

function SectionClashOfTeams() {
  return (
    <div>
      <Hero tag="Tournaments" title="Clash of Teams">A multi-week, multi-team competition where clubs battle head-to-head across rotating weekly fixtures — with live standings, team branding, and a real-time live dashboard.</Hero>
      <H2>What is Clash of Teams?</H2>
      <CardGrid>
        <Card icon="🏆" title="Multi-Week Format">Run over 4–8+ weeks with weekly round fixtures, each tracked independently.</Card>
        <Card icon="👥" title="Team-Based">2–4+ teams compete with full rosters rotating through weekly matchups.</Card>
        <Card icon="📊" title="Live Dashboard">Fullscreen live view showing standings, weekly results, scores, and match matrix.</Card>
        <Card icon="🎨" title="Team Branding">Each team gets a custom name, colour, and logo shown throughout.</Card>
      </CardGrid>
      <HR/>
      <H2>Step-by-Step Setup</H2>
      <H3>Step 1 — Create a New Clash</H3>
      <Steps steps={[
        {title:"Enter a Tournament Title",body:"Give your Clash a name (e.g. Thurs League Battle – Season 1)."},
        {title:"Set a Public URL (optional)",body:"Auto-generated from the title. You can customise or leave blank."},
        {title:"Select Sport",body:"Choose the sport (e.g. Padel). Click Next."},
      ]}/>
      <H3>Step 2 — Format & Structure</H3>
      <Steps steps={[
        {title:"Choose Match Format",body:"Doubles (2v2) or Singles (1v1). ⚠ This is LOCKED once set."},
        {title:"Set Number of Teams",body:"Enter how many teams will compete (e.g. 4 teams)."},
        {title:"Set Number of Weeks",body:"Enter total weeks (e.g. 8). Each week is a separate round of fixtures."},
        {title:"Review & Confirm",body:"Check Title, Sport, Format, Teams, and Weeks carefully before proceeding."},
      ]}/>
      <Callout type="warning" icon="⚠️"><strong>Match Format is locked once set.</strong> Double-check Doubles vs Singles before proceeding.</Callout>
      <H3>Step 3 — Basics Tab</H3>
      <Table
        headers={["Field","Description","Editable?"]}
        rows={[
          ["Public URL","Shareable live view link","✅ Yes"],
          ["Tournament Title","Public name of the Clash","✅ Yes"],
          ["Match Format","Doubles or Singles","❌ Locked"],
          ["Weeks","Total competition weeks","✅ Yes"],
          ["Show matrix on live dashboard","Toggles head-to-head results grid","✅ Yes"],
        ]}
      />
      <H3>Step 4 — Configure Teams</H3>
      <CardGrid>
        <Card icon="📝" title="Team Name">Full name shown on the live dashboard and fixtures.</Card>
        <Card icon="🔤" title="Short Name">Abbreviated version for compact views and the results matrix.</Card>
        <Card icon="🎨" title="Team Colour">Hex colour shown throughout the live view and standings.</Card>
        <Card icon="🖼️" title="Team Logo">Upload a logo image. Shown prominently on the live dashboard header.</Card>
      </CardGrid>
      <H3>Step 5 — Build the Roster</H3>
      <Steps steps={[
        {title:"Add an existing player",body:"Use the Add existing player search to find registered users. Click + Add."},
        {title:"Create & Add a new player",body:"Type a new player name and click Create & Add to create an account simultaneously."},
        {title:"Remove a player",body:"Click the red delete icon next to any player's name."},
      ]}/>
      <H3>Step 6 — Create Fixtures</H3>
      <Steps steps={[
        {title:"Select a week",body:"Click the week tab. Completed weeks show done status."},
        {title:"Create a match",body:"Click + Create Match to add a new fixture for that week."},
        {title:"Organise by rounds",body:"Matches are grouped into Round 1, Round 2 etc. with multiple courts simultaneously."},
        {title:"Enter scores",body:"Enter results after matches complete. Points update on the live dashboard automatically."},
      ]}/>
      <HR/>
      <H2>Live Dashboard</H2>
      <Table
        headers={["Element","Description"]}
        rows={[
          ["<strong>Team Score Headers</strong>","Logo, rank, total points (PTS), and points this week (THIS WK) — updating live"],
          ["<strong>Standings Table</strong>","Teams ranked by points with W, L, GD, PTS columns"],
          ["<strong>Recent Results</strong>","Live feed of latest match results with player names and scores"],
          ["<strong>Weekly Matrix</strong>","All weeks (Week 1–8) with head-to-head results and UPCOMING status"],
        ]}
      />
    </div>
  );
}

function SectionRyderCup() {
  return (
    <div>
      <Hero tag="Tournaments" title="Ryder Cup Tournament">Two teams, captain-led matchups, and live point tracking — bringing the strategy and prestige of the legendary Ryder Cup format into racquet sports.</Hero>
      <H2>Core Concept</H2>
      <CardGrid>
        <Card icon="⚔️" title="Two Teams">Two competing teams, each with multiple players.</Card>
        <Card icon="🎾" title="Individual Matches">Players compete in singles or doubles. Each match contributes 1 point to the team total.</Card>
        <Card icon="🧮" title="Points-Based Winning">The team with the most points at the end wins the Ryder Cup.</Card>
        <Card icon="👑" title="Captain Strategy">Captains decide who plays when, creating a powerful strategy layer.</Card>
      </CardGrid>
      <H2>Tournament Structure — 4 Rounds</H2>
      <Steps steps={[
        {num:"R1",title:"Round 1 — Captain A picks first",body:"Captain A selects a player/pair. Captain B responds. Winner earns 1 point."},
        {num:"R2",title:"Round 2 — Captain B picks first",body:"Same format but Captain B now has first pick, shifting strategic dynamic."},
        {num:"R3",title:"Round 3 — Spin the Wheel (Random)",body:"Matchups are randomised, removing the strategic advantage of picking order."},
        {num:"R4",title:"Round 4 — Alternating Picks",body:"Captains take turns selecting, maximising strategic depth in the final round."},
      ]}/>
      <H2>Admin Setup Screen</H2>
      <Table
        headers={["Element","Description"]}
        rows={[
          ["<strong>Overall Score Banner</strong>","Live running score at the top (e.g. Captain Cook 1 – 0 Captain Sparrow)"],
          ["<strong>Round Labels</strong>","Each round labelled with its mechanic (e.g. Captain A picks first, Spin wheel)"],
          ["<strong>Match Cards</strong>","Both teams' players shown side by side. Winner highlighted in green"],
          ["<strong>Enter Score</strong>","Click to input a result. Completed scores show Edit Score option"],
        ]}
      />
      <H2>Live Ryder Cup View</H2>
      <Table
        headers={["Element","Description"]}
        rows={[
          ["<strong>Round Panels (R1–R4)</strong>","Each round has its own panel with fixtures, VS matchups, and scores"],
          ["<strong>Overall Score</strong>","Large centrepiece showing live team totals"],
          ["<strong>Round Scores</strong>","Each round panel shows its own mini-score"],
          ["<strong>Full Screen</strong>","Expands for display on a venue screen or projector"],
          ["<strong>LIVE indicator</strong>","Green badge confirms real-time updates"],
        ]}
      />
      <Callout type="tip" icon="💡">Display the <strong>Live Ryder Cup View</strong> on a large screen at your venue — it creates a genuine match-day atmosphere.</Callout>
    </div>
  );
}

function SectionMatchCentre() {
  return (
    <div>
      <Hero tag="Tournaments" title="Match Centre">Your desk on match day: check players in, see every court at a glance, and know when the event will actually finish.</Hero>
      <H2>What the Match Centre is for</H2>
      <P>The draw tells you who plays whom. The Match Centre runs the day itself — who has arrived, who has paid, which court is free, which match is late, and what time you will be done. It opens on check-in and switches to the board once people start arriving.</P>
      <CardGrid>
        <Card icon="✅" title="Check-in">Tap a player's name as they arrive. Tap again to undo.</Card>
        <Card icon="⏰" title="Late & no-show">Flag someone running late, or resolve their matches if they never come.</Card>
        <Card icon="💷" title="Fee due">Record who has paid at the door, match by match.</Card>
        <Card icon="📺" title="Live view">Put the public scoreboard on the venue screen, or stream it.</Card>
      </CardGrid>
      <HR/>
      <H2>Check-in</H2>
      <P>Every pair or team in the event is listed by group. Click a player's <strong>name</strong> to mark them arrived. <strong>Late</strong> flags someone on their way, <strong>Fee due · Paid?</strong> records their entry fee, and <strong>Mark as no-show</strong> resolves their matches so the rest of the draw can carry on. Everything saves as you tap — there is no save button.</P>
      <Shot src={shotMatchCentreCheckin} alt="Player check-in, with pairs listed by group"
            caption="Check-in: each pair shows its two players, a late flag, the entry fee and a no-show action. The counter at the top tracks how many pairs are ready."/>
      <H2>The board</H2>
      <P>Once you are under way, the board is the view you keep open. Set how many <strong>courts</strong> you have and how long a <strong>match</strong> takes, and the timeline lays every fixture out across them — what is on now, what is queued, and which knockout rounds are still waiting on qualifiers.</P>
      <Shot src={shotMatchCentreBoard} alt="The Match Centre board with a court timeline and day summary"
            caption="The board: courts down the side, time across the top. The summary reads the plan back to you — projected finish, worst delay, when the last court clears, and how long the whole tournament will take."/>
      <H3>What the day summary tells you</H3>
      <Table headers={["Figure","What it means"]} rows={[
        ["Now","The current time against the schedule — before you start, it says the tournament has not been anchored yet."],
        ["Projected finish","When the last match ends if everything from here runs to its slot."],
        ["Worst delay","How far behind the most delayed court is. It starts counting at kick-off."],
        ["Last court off","The court that finishes last, and at what time."],
        ["Tournament time","How long the event lasts end to end — first match to final."],
      ]}/>
      <Callout type="tip" icon="💡">Knockout rounds appear on the timeline before their teams are known, shown as TBD. Without them the projected finish would be the end of the group stage, not the end of your day.</Callout>
      <H2>Plans</H2>
      <P>Check-in and the match-day desk come with <strong>Starter</strong>. The timeline, lateness tracking and disputes are part of <strong>Plus</strong>.</P>
    </div>
  );
}

function SectionTeamSports() {
  return (
    <div>
      <Hero tag="Sports" title="Team Sports">Football, basketball, cricket, volleyball, esports and roundnet each get their own part of ScorePal, with the scoring and tables that sport actually uses.</Hero>
      <H2>A section of your own</H2>
      <P>Racquet sports enter players and pairs and score in sets. A football league does not work that way — it enters clubs, records one final score, and ranks on points with goal difference. So those sports get their own area: your dashboard, leagues, tournaments, players and squads all live inside it, set up for that sport from the start.</P>
      <CardGrid>
        <Card icon="⚽" title="Football">Goals, 3 points for a win and 1 for a draw, and a table with goals for, against and difference.</Card>
        <Card icon="🏀" title="Basketball">Points, 2 for a win, a played–won–lost record and a points difference. Scores can be entered by quarter.</Card>
        <Card icon="🏏" title="Cricket">Runs, 2 points for a win and 1 for a tie.</Card>
        <Card icon="🏐" title="Volleyball">Best of three or five, ranked on the official 3-2-1-0 points table.</Card>
        <Card icon="🎮" title="Esports">A series of maps, played by teams or 1v1 — you choose per competition.</Card>
        <Card icon="🥏" title="Roundnet">2v2 pairs, best of three games to 21, three points a win.</Card>
      </CardGrid>
      <HR/>
      <H2>What is different inside a team-sport area</H2>
      <Table headers={["","Racquet sports","Team sports"]} rows={[
        ["Who enters","Players and pairs","Named clubs and squads, with positions"],
        ["Score entry","Set by set","One final score per side (or maps for esports, sets for volleyball)"],
        ["Standings","Win % or your own points system","The table that sport uses, including draws where the sport has them"],
        ["Draws","Any format","The same formats, with draws allowed in football, hockey, rugby and cricket"],
      ]}/>
      <Callout type="info" icon="🏃">Running is set up differently again: it runs races with entries, race-day check-in, results upload and season points — not leagues and draws.</Callout>
      <H2>Getting in</H2>
      <P>Pick your sport when you sign up as an organiser and ScorePal opens on that sport's dashboard, with its leagues, tournaments, players and teams. Everything else in this guide — registration, payments, the Match Centre, live views — works the same way there.</P>
    </div>
  );
}

function SectionFormats() {
  return (
    <div>
      <Hero tag="Tournaments" title="More Formats">Americano, Mexicano, King of the Court, Swiss and double elimination — what each one is, and who it suits.</Hero>
      <H2>Social formats</H2>
      <P>These are built for an evening where everyone plays, rather than a bracket where half your entrants go home early.</P>
      <CardGrid>
        <Card icon="🔁" title="Americano">Partners change every round and everyone plays with and against everyone. Ranked individually on points won.</Card>
        <Card icon="📈" title="Mexicano">Like Americano, but the next round is drawn from the standings — first plays fourth, second plays third, so games stay close.</Card>
        <Card icon="👑" title="King of the Court">Courts are ranked. Win and you move up towards the King Court; lose and you drop down.</Card>
      </CardGrid>
      <Shot src={shotAmericano} alt="An Americano schedule of four rounds with rotating partners"
            caption="An eight-player Americano: four rounds, two matches a round, and nobody keeps the same partner twice."/>
      <Callout type="tip" icon="💡">Americano, Mexicano and King of the Court generate as you go — the next round is worked out once the last one has scores, so a late change doesn't wreck the schedule.</Callout>
      <HR/>
      <H2>Competitive formats</H2>
      <CardGrid>
        <Card icon="🇨🇭" title="Swiss">Everyone plays the same number of rounds, paired each round against someone on a similar record. No eliminations, and byes avoided where it can.</Card>
        <Card icon="🥈" title="Double elimination">Lose once and you drop to the losers' bracket; lose twice and you are out. Winners, losers and a grand final.</Card>
        <Card icon="🎽" title="Consolation draw">A second bracket for first-round losers, so a long trip isn't one match long.</Card>
      </CardGrid>
      <H2>Groups into a knockout</H2>
      <P>The most common tournament shape: a group stage where everyone gets matches, then a bracket for those who qualify. The draws screen shows both, with the qualifying line drawn on each group so players can see what they need.</P>
      <Shot src={shotDrawsGroups} alt="Group tables with a qualifying line, and fixtures listed by round"
            caption="Group stage: standings on the left with the qualifying line marked, fixtures by round on the right. Scores go in from either side."/>
      <HR/>
      <H2>Choosing a format</H2>
      <Table headers={["Format","Best for","Eliminations"]} rows={[
        ["Round robin","Small events where everyone should play everyone","None"],
        ["Groups + knockout","Most club tournaments","After the group stage"],
        ["Knockout","Big fields and short days","Immediate"],
        ["Double elimination","Serious competition with a second chance","After two losses"],
        ["Swiss","Large fields, everyone plays every round","None"],
        ["Americano / Mexicano","Social nights and mixers","None"],
        ["King of the Court","Fast, informal club sessions","None"],
        ["Ladder","Ongoing club competition with challenges","None"],
      ]}/>
      <Callout type="info" icon="🧮">Every format has a free generator on the website, so you can build a schedule and see how it looks before creating anything.</Callout>
    </div>
  );
}

function SectionLeague() {
  return (
    <div>
      <Hero tag="Leagues" title="Create a League">Creating a league follows a simple 3-step process — from choosing a format to configuring scoring rules.</Hero>
      <H2>Step 1 — Select Sport & League Type</H2>
      <Steps steps={[
        {num:"A",title:"Choose a Sport",body:"Select from: Padel, Pickleball, Squash, Badminton, or Tennis."},
        {num:"B",title:"Choose a League Type",body:"Win % League (ranked by win percentage), Points League (earn points per win/set), or Ladder League (challenge-based, upgrade required)."},
        {num:"C",title:"Choose Game Scheduling",body:"Manual Games (flexible) or Scheduled Games (auto-generated fixtures, upgrade required)."},
      ]}/>
      <H2>Step 2 — Configure League</H2>
      <Table
        headers={["Field","Description","Required"]}
        rows={[
          ["League Name","Public name (e.g. Summer League 2026)","<span style='background:#fef3c7;color:#92400e;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:600'>Required</span>"],
          ["Participants","Singles, Doubles, or Team-based","<span style='background:#fef3c7;color:#92400e;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:600'>Required</span>"],
          ["How Many Sets","1 Set, 3 Sets, or 5 Sets","<span style='background:#fef3c7;color:#92400e;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:600'>Required</span>"],
          ["Entry Deadline","Last date players can register","<span style='background:#fef3c7;color:#92400e;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:600'>Required</span>"],
          ["Start & End Date","Defines the league season duration","<span style='background:#fef3c7;color:#92400e;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:600'>Required</span>"],
        ]}
      />
      <H2>Step 3 — Scoring (Points League only)</H2>
      <Table
        headers={["Setting","Description","Example"]}
        rows={[
          ["Points for a Win","Points awarded for winning a match","3 points"],
          ["Points for Winning a Set","Who earns set points","All players"],
          ["Points per Set","Points awarded per set won","1 point per set"],
        ]}
      />
      <Callout type="tip" icon="💡">Set up scoring rules <strong>before</strong> inviting players to avoid confusion or disputes mid-season.</Callout>
    </div>
  );
}

function SectionLeagueOptions() {
  return (
    <div>
      <Hero tag="Leagues" title="League Options">A reference guide to every setting available when configuring a league in ScorePal.</Hero>
      <H2>League Level</H2>
      <P>Sets the standard of the league publicly and auto-selects the manual players' starting ranking.</P>
      <H2>Participants</H2>
      <Table
        headers={["Option","Description","Best For"]}
        rows={[
          ["<strong>Singles</strong>","Individual rankings only","Standard 1v1 competition"],
          ["<strong>Doubles – Individual Ranking</strong>","Players ranked individually; no fixed partner needed","Social doubles leagues"],
          ["<strong>Doubles – Team Ranking</strong>","Fixed pairs ranked as a team throughout the season","Committed partnerships"],
        ]}
      />
      <H2>Type of League</H2>
      <Table
        headers={["League Type","Ranking Method","Scheduling"]}
        rows={[
          ["<strong>Win % — Manual</strong>","Win % (Wins ÷ Played)","All games manually created"],
          ["<strong>Win % — Scheduled</strong>","Win %","Games auto-scheduled"],
          ["<strong>Points — Manual</strong>","Points per win/set","All games manually created"],
          ["<strong>Points — Scheduled</strong>","Points per win/set","Games auto-scheduled"],
          ["<strong>Ladder — Challenge Based</strong>","Position based on challenges","Via the platform"],
        ]}
      />
      <H2>Enable League Ranking</H2>
      <CardGrid>
        <Card icon="✅" title="Selected">Players' results count towards ELO and SPPR ratings after each match.</Card>
        <Card icon="⬜" title="Not Selected">Results won't affect ratings. Useful for casual or practice leagues.</Card>
      </CardGrid>
      <H2>Match Types</H2>
      <CardGrid>
        <Card icon="👨" title="Men's Doubles">Male players in doubles format.</Card>
        <Card icon="👩" title="Women's Doubles">Female players in doubles format.</Card>
        <Card icon="🤝" title="Mixed Doubles">Male and female players paired together.</Card>
        <Card icon="🧍" title="Men's Singles">Male players in 1v1 format.</Card>
        <Card icon="🧍‍♀️" title="Women's Singles">Female players in 1v1 format.</Card>
      </CardGrid>
    </div>
  );
}

function SectionLadder() {
  return (
    <div>
      <Hero tag="Leagues" title="Ladder League">A challenge-based ranking system where players move up or down by defeating or losing to nearby opponents on the ladder.</Hero>
      <H2>How a Ladder Works</H2>
      <Steps steps={[
        {title:"Initial Ranking",body:"Players placed on the ladder at the start of the season — random, seeded, or by skill level."},
        {title:"Challenge System",body:"Players can challenge others ranked above them (usually within 1–3 positions). Challenges arranged via the platform."},
        {title:"Match Play",body:"The two players compete following standard scoring rules for the sport and format."},
        {title:"Position Movement",body:"Lower-ranked player wins → moves up to the higher player's position. Higher-ranked player wins → positions unchanged."},
        {title:"Ongoing Updates",body:"The ladder updates continuously — no fixed end-of-round reset. There's always a next target."},
      ]}/>
      <H2>Why Use a Ladder?</H2>
      <CardGrid>
        <Card icon="⚡" title="Encourages Competitive Play">Players are always motivated to challenge upward.</Card>
        <Card icon="📅" title="Structured Scheduling">Challenges tracked through the platform without manual fixture lists.</Card>
        <Card icon="📊" title="Clear Rankings">Every player knows exactly where they stand at any point.</Card>
      </CardGrid>
      <Callout type="info" icon="ℹ️">The Ladder League format requires an <strong>upgrade</strong> from the Free plan — available on Starter and above.</Callout>
    </div>
  );
}

function SectionDivision() {
  return (
    <div>
      <Hero tag="Leagues" title="Division Season">Multi-division leagues that group players by skill level, each running their own round-robin, with promotion and relegation at season end.</Hero>
      <H2>How Division Seasons Work</H2>
      <P>A <strong>Season</strong> contains multiple <strong>divisions</strong>, each with its own participants, standings, and matches. Each division runs its own round-robin league.</P>
      <H2>Standings Table Columns</H2>
      <Table
        headers={["Column","Meaning"]}
        rows={[
          ["<strong>P</strong>","Matches played"],
          ["<strong>W</strong>","Matches won"],
          ["<strong>GW</strong>","Games won"],
          ["<strong>GL</strong>","Games lost"],
          ["<strong>GD</strong>","Game difference"],
          ["<strong>PTS</strong>","League points"],
          ["<strong>Form</strong>","Recent match results (e.g. W W L W)"],
        ]}
      />
      <H2>Promotion & Relegation</H2>
      <CardGrid>
        <Card icon="⬆️" title="Promotion">Top N teams move up to the next higher division for the following season.</Card>
        <Card icon="⬇️" title="Relegation">Bottom N teams move down to the next lower division for the following season.</Card>
      </CardGrid>
      <Callout type="tip" icon="💡">Example: setting <strong>Promoted count: 2</strong> and <strong>Relegated count: 2</strong> means the top and bottom 2 teams move each season end.</Callout>
      <Callout type="info" icon="ℹ️">Division Seasons are available on the <strong>Pro organiser plan</strong>, which supports unlimited divisions and entries.</Callout>
    </div>
  );
}

function SectionLeagueOfLeagues() {
  return (
    <div>
      <Hero tag="Leaderboards" title="League of Leagues">A global monthly ranking of leagues based on Competitiveness, Participation, and Activity — with coins awarded to the winner each month.</Hero>
      <H2>What is League of Leagues?</H2>
      <CardGrid>
        <Card icon="🌍" title="Global Ranking">Leagues ranked worldwide. Filter by Global, Country, City, or Sport.</Card>
        <Card icon="📅" title="Monthly & Yearly">Switch between Monthly (rolling 4-week) and Yearly views.</Card>
        <Card icon="🏅" title="Monthly Prize">The winning league earns <strong>1,000 SP Coins</strong> each month.</Card>
        <Card icon="⚙️" title="Opt-In">Organisers choose to enter their league from their league settings.</Card>
      </CardGrid>
      <H2>The Three Scoring Pillars</H2>
      <H3>1 — Competitiveness Score</H3>
      <P>How closely contested the matches in your league are — the ratio of close vs one-sided results.</P>
      <P>Example: <strong>7 Close / 24 Not Close</strong> across 31 total matches. A higher ratio of close matches earns more coins.</P>
      <Callout type="tip" icon="💡">Improve by ensuring players are well-matched. Use player ratings and divisions to keep games close.</Callout>
      <H3>2 — Participation Score</H3>
      <P>The proportion of registered players who are actively playing. Example: <strong>36/70 — 51% active players</strong>.</P>
      <Callout type="tip" icon="💡">Boost by chasing up inactive members and keeping the schedule consistent.</Callout>
      <H3>3 — Activity Score</H3>
      <P>Average matches per active player. Example: <strong>0.9 matches per active player</strong>.</P>
      <Callout type="tip" icon="💡">Increase by running more frequent fixtures and enabling matchmaking between members.</Callout>
      <H2>Leaderboard Columns</H2>
      <Table
        headers={["Column","Meaning"]}
        rows={[
          ["<strong>Rank</strong>","Current position for the selected period and filter"],
          ["<strong>League</strong>","League name and organiser"],
          ["<strong>Comp</strong>","Competitiveness Score coins"],
          ["<strong>Part</strong>","Participation Score coins"],
          ["<strong>Activity</strong>","Activity Score coins"],
          ["<strong>Points</strong>","Total coins — the primary ranking metric"],
        ]}
      />
    </div>
  );
}

function SectionLeagueOfPlayers() {
  return (
    <div>
      <Hero tag="Leaderboards" title="League of Players">A global player leaderboard where individuals earn coins for every action — playing matches, winning, logging in daily, and inviting friends — then climb tiers and redeem rewards.</Hero>
      <H2>Leaderboard Columns</H2>
      <Table
        headers={["Column","Meaning"]}
        rows={[
          ["<strong>Rank</strong>","Current position for the selected period and filter"],
          ["<strong>Player</strong>","Name, profile photo, and country flag"],
          ["<strong>Games</strong>","Total matches played"],
          ["<strong>Wins</strong>","Total match wins"],
          ["<strong>Streaks</strong>","Weekly streaks achieved (3+ games in a week)"],
          ["<strong>Coins</strong>","Total coins — primary ranking metric"],
        ]}
      />
      <H2>Earning Coins</H2>
      <Table
        headers={["Action","Coins","Notes"]}
        rows={[
          ["🎾 <strong>Play a Match</strong>","+10 coins","Ranked games only"],
          ["🏆 <strong>Win a Match</strong>","+10 bonus","In addition to the 10 for playing"],
          ["🔥 <strong>Weekly Streak</strong>","+15 coins","Play 3+ games in a single week"],
          ["👥 <strong>Invite a Friend</strong>","+50 coins","Once invited player completes 2+ games"],
          ["⬆️ <strong>Beat Higher Ranked</strong>","+25 coins","Beat a player with a higher SPPR rating"],
          ["🤝 <strong>Match Making</strong>","+10 extra","Via the Matchmaking feature only"],
          ["📅 <strong>Daily Login</strong>","+2 coins/day","Log in every day"],
        ]}
      />
      <H2>Coin Tiers</H2>
      <Table
        headers={["Tier","Coins Required"]}
        rows={[
          ["🥉 <strong>Bronze</strong>","300 – 499 coins"],
          ["🥈 <strong>Silver</strong>","500 – 1,499 coins"],
          ["🥇 <strong>Gold</strong>","1,500 – 2,999 coins"],
          ["💎 <strong>Platinum</strong>","3,000+ coins"],
        ]}
      />
      <Callout type="tip" icon="💡">Fastest way to climb: combine <strong>daily logins</strong> (+2/day), <strong>weekly streaks</strong> (+15), and <strong>inviting friends</strong> (+50 per referral).</Callout>
    </div>
  );
}

function SectionClubLeaderboard() {
  return (
    <div>
      <Hero tag="Leaderboards" title="Club Leaderboard">A private, club-scoped leaderboard that works exactly like League of Players — but only shows players within your community to drive retention and engagement.</Hero>
      <H2>How It Differs from League of Players</H2>
      <Table
        headers={["","League of Players","Club Leaderboard"]}
        rows={[
          ["<strong>Who is ranked</strong>","All enrolled players globally","Your club members only"],
          ["<strong>Visibility</strong>","Public — anyone can view","Private — club members only"],
          ["<strong>Coin system</strong>","Identical","Identical"],
          ["<strong>Tiers</strong>","Bronze → Platinum","Bronze → Platinum"],
          ["<strong>Purpose</strong>","Platform-wide prestige","Club retention & engagement"],
        ]}
      />
      <Callout type="info" icon="ℹ️">Players appear on <strong>both</strong> the global League of Players and your Club Leaderboard simultaneously — the same activity counts towards both.</Callout>
      <H2>For Club Administrators</H2>
      <Steps steps={[
        {title:"Enable the Club Leaderboard",body:"Turn on from your Community or Club profile settings. All active members are automatically included."},
        {title:"Promote it to your members",body:"Share the leaderboard link via WhatsApp, Instagram, or in-app messaging."},
        {title:"Incentivise top performers",body:"Use club prizes — free court booking, merchandise, or a trophy — to drive friendly competition."},
        {title:"Encourage daily logins and streaks",body:"Remind members that logging in daily (+2) and maintaining a weekly streak (+15) counts."},
        {title:"Track engagement over time",body:"Monitor how the leaderboard affects match frequency, login rates, and referrals from your Club Dashboard."},
      ]}/>
      <Callout type="tip" icon="💡">The <strong>Invite a Friend</strong> action (+50 coins) is the highest single earner — encourage members to refer new players.</Callout>
    </div>
  );
}

function SectionMatch() {
  return (
    <div>
      <Hero tag="Match Making" title="Create a Match">Create and join games in seconds — competitive or casual, singles or doubles, at any skill level.</Hero>
      <H2>How to Create a Match</H2>
      <Steps steps={[
        {title:"Start a Match",body:"Go to Match Making and click Start a match."},
        {title:"Choose Venue Setup",body:"Select 'I already know the venue' if you've already booked a court. Click Next."},
        {title:"Enter Match Details",body:"Sport, Type (Singles/Doubles), Date & Time, Duration (60/90/120 min), Location, Title (optional), Venue/Court Name, Price per player. Click Next."},
        {title:"Configure Your Match",body:"Match Type (Competitive = affects ratings, Friendly = no rating impact). Set Skill Level Range and Player Preferences (All/Men/Women). Toggle court as booked or set as private."},
        {title:"Create Match",body:"Click Create match to publish. Now discoverable by players matching your criteria."},
      ]}/>
      <H2>How to Join a Match</H2>
      <Steps steps={[
        {title:"Browse Matches",body:"Go to Match Making → Find Games. Filter by sport, venue, etc."},
        {title:"View Match Details",body:"Each match shows date & time, location, skill level range, available slots, and match type."},
        {title:"Join a Match",body:"Open the match and tap an available slot to join."},
      ]}/>
      <H2>Match Overview Actions</H2>
      <CardGrid>
        <Card icon="✏️" title="Edit Match">Available if you created it — update details before it fills.</Card>
        <Card icon="🚪" title="Leave Game">Remove yourself from a match you've joined.</Card>
        <Card icon="📅" title="Add to Calendar">Save the match to your personal calendar.</Card>
        <Card icon="💬" title="Contact Organiser">Reach out to the match creator directly.</Card>
      </CardGrid>
    </div>
  );
}

function SectionInvite() {
  return (
    <div>
      <Hero tag="Match Making" title="Invite Players">Fill your matches and grow your playing network — invite players via a shareable link or direct email.</Hero>
      <H2>Option 1 — Share an Invite Link</H2>
      <Steps steps={[
        {title:"Open the Invite Player window",body:"Access from within any match, league, or tournament you've created."},
        {title:"Copy your unique invite link",body:"Click the copy button next to the generated link."},
        {title:"Share it anywhere",body:"Send via WhatsApp, SMS, social media, or any messaging app."},
      ]}/>
      <P>The recipient clicks the link → signs up → can join matches and play with you straight away.</P>
      <HR/>
      <H2>Option 2 — Invite via Email</H2>
      <Steps steps={[
        {title:"Enter the player's name",body:"Type their full name in the Name field."},
        {title:"Enter their email address",body:"Type or paste their email in the Email field."},
        {title:"Click 'Send invitation'",body:"Player receives an email invite and can sign up directly from it."},
      ]}/>
      <Table
        headers={["Method","Best For","Speed"]}
        rows={[
          ["<strong>Invite Link</strong>","Group chats, WhatsApp, social media","⚡ Instant"],
          ["<strong>Email Invite</strong>","Direct, personal invitations","📬 A few minutes"],
        ]}
      />
    </div>
  );
}

function SectionReferrals() {
  return (
    <div>
      <Hero tag="Match Making" title="Referral Program">Invite players to join ScorePal, earn real cash rewards once they've played 2 games — and track every referral, conversion, and reward in one place.</Hero>
      <H2>Reward Tiers</H2>
      <Table
        headers={["Players Referred","Cash Reward"]}
        rows={[
          ["<strong>10 players</strong>","Earn $20"],
          ["<strong>25 players</strong>","Earn $50"],
          ["<strong>50 players</strong>","Earn $125"],
          ["<strong>100 players</strong>","Earn $250"],
        ]}
      />
      <Callout type="info" icon="ℹ️">A referral is only confirmed once the invited player has played <strong>2 or more games</strong>.</Callout>
      <H2>Your Referral Dashboard</H2>
      <Table
        headers={["Card","What It Shows"]}
        rows={[
          ["<strong>Referrals</strong>","Confirmed referrals (signed up and played 2+ games)"],
          ["<strong>Reward Earned</strong>","Total cash reward earned so far"],
          ["<strong>Next Reward</strong>","Next cash milestone and players needed to reach it"],
          ["<strong>Conversion Rate</strong>","Link clicks vs sign-ups"],
        ]}
      />
      <H2>How to Invite Players</H2>
      <H3>Option 1 — Email</H3>
      <Steps steps={[
        {title:"Enter email addresses",body:"Type or paste into the Invite players by email field."},
        {title:"Send the invitation",body:"Click the send button. Player receives a direct email with your referral tracked automatically."},
      ]}/>
      <H3>Option 2 — Referral Link</H3>
      <Steps steps={[
        {title:"Copy your unique link",body:"Your personal referral URL (e.g. scorepal.co/r/01412ff5ace4). Click the copy icon."},
        {title:"Share anywhere",body:"Via Facebook, WhatsApp, Twitter/X using the social icons, or paste into any message."},
      ]}/>
      <Callout type="tip" icon="💡">Chase up <strong>pending</strong> referrals — they've signed up but haven't played 2 games yet. One message encouraging them to join a match converts them to confirmed.</Callout>
    </div>
  );
}

function SectionWallet() {
  return (
    <div>
      <Hero tag="Account" title="Wallet">Your ScorePal Wallet holds your cash balance from the referral programme and your ScorePal Coins earned through the League of Leagues.</Hero>
      <H2>How to Access</H2>
      <Steps steps={[
        {title:"Click your profile avatar",body:"Select your profile picture or initials in the top right corner."},
        {title:"Select Wallet",body:"Click Wallet from the dropdown menu (alongside Dark Mode, Edit Profile, Subscription, and Sign out)."},
      ]}/>
      <HR/>
      <H2>Wallet Dashboard</H2>
      <Table
        headers={["Card","What It Shows","Source"]}
        rows={[
          ["<strong>Total Balance</strong>","Cash earnings available to withdraw","Player Referral Programme"],
          ["<strong>ScorePal Coins</strong>","Coin balance from competitive league performance","League of Leagues score"],
        ]}
      />
      <H3>Setting Up Your Payout Account</H3>
      <Callout type="info" icon="ℹ️">You must complete onboarding before withdrawing. Click <strong>Open Payments Settings</strong> to begin.</Callout>
      <Steps steps={[
        {title:"Click Open Payments Settings",body:"Launches onboarding to register your bank account or payment provider."},
        {title:"Complete onboarding",body:"Verify identity and link your payout method."},
        {title:"Withdraw your balance",body:"Once verified, the Withdraw button becomes active."},
      ]}/>
      <Callout type="tip" icon="💡"><strong>ScorePal Coins redemption is coming soon.</strong> Keep earning coins through your leagues to be ready when redemption launches.</Callout>
      <H2>User Menu Options</H2>
      <Table
        headers={["Option","Description"]}
        rows={[
          ["<strong>Dark Mode</strong>","Toggle light/dark interface theme"],
          ["<strong>Edit Profile</strong>","Update player information, photo, and location"],
          ["<strong>Subscription</strong>","View or manage your current plan"],
          ["<strong>Wallet</strong>","Access cash balance and ScorePal Coins"],
          ["<strong>Sign Out</strong>","Log out of your ScorePal account"],
        ]}
      />
    </div>
  );
}

function SectionPayments() {
  return (
    <div>
      <Hero tag="Account" title="Payments">Connect a Stripe account to receive payments for court bookings, league enrolments, and tournament entry fees directly through ScorePal.</Hero>
      <H2>What Payments Covers</H2>
      <CardGrid>
        <Card icon="🏟️" title="Court Bookings">Charge members for court reservations. Payments go directly to your payout account.</Card>
        <Card icon="📋" title="League Enrolments">Collect entry fees when players register for your leagues.</Card>
        <Card icon="🏆" title="Tournament Entry Fees">Accept payments for tournament registration.</Card>
        <Card icon="🔒" title="Powered by Stripe">Globally trusted, secure payment processing.</Card>
      </CardGrid>
      <H2>Setting Up Your Payout Account</H2>
      <Callout type="warning" icon="⚠️">You must complete payout onboarding before receiving payments. Until verified, Details submitted: No and Account verified: No will show.</Callout>
      <Steps steps={[
        {title:"Select your Bank Country",body:"Choose the country where your bank account is held."},
        {title:"Click Create Pay Out Account",body:"Launches the Stripe onboarding process."},
        {title:"Complete Stripe onboarding",body:"Verify identity, provide business details, and link your bank account (5–10 minutes)."},
        {title:"Account verified",body:"Status updates to Details submitted: Yes and Account verified: Yes."},
      ]}/>
      <H2>Payout Profile Status</H2>
      <Table
        headers={["Status","Meaning"]}
        rows={[
          ["<strong>Details submitted: No</strong>","Onboarding not completed — click Create Pay Out Account"],
          ["<strong>Details submitted: Yes</strong>","Details sent to Stripe for review"],
          ["<strong>Account verified: No</strong>","Not yet verified — cannot receive payments"],
          ["<strong>Account verified: Yes</strong>","Fully verified and ready to accept payments"],
        ]}
      />
      <Callout type="tip" icon="💡">Set up your payout account <strong>before</strong> creating paid events.</Callout>
    </div>
  );
}

function SectionCourtBookings() {
  return (
    <div>
      <Hero tag="Account" title="Court Bookings">Configure courts, opening hours, peak/off-peak pricing, and manage all member bookings from one central dashboard.</Hero>
      <H2>Step 1 — Amenities & Opening Hours</H2>
      <H3>Amenities</H3>
      <P>Use the Amenities dropdown to select facilities available at your club (e.g. surfaces, lighting, changing rooms). Displayed on your public club profile.</P>
      <H3>Opening Hours</H3>
      <Table
        headers={["Day","Setting"]}
        rows={[
          ["Monday – Sunday","Set opening and closing times for each individual day"],
          ["* Holidays","Set specific hours for public holidays, or mark as closed"],
        ]}
      />
      <Callout type="info" icon="ℹ️">Members can only book during the hours you define here.</Callout>
      <H2>Step 2 — Peak Hours, Pricing & Booking Terms</H2>
      <H3>Peak Hours Grid</H3>
      <P>Click cells across the time grid (00:00–23:00) to mark them as peak. Highlighted = peak hours. Scroll horizontally for the full 24-hour range.</P>
      <H3>Pricing</H3>
      <Table
        headers={["Field","Description"]}
        rows={[
          ["<strong>Off-peak hour price</strong>","Price per hour during off-peak times in AED, EUR, GBP, USD"],
          ["<strong>Peak hour price</strong>","Price per hour during peak times in AED, EUR, GBP, USD"],
          ["<strong>Default currency</strong>","Primary currency shown to members when booking"],
        ]}
      />
      <Callout type="warning" icon="⚠️">You must set up a Stripe payout account before enabling payment handling.</Callout>
      <H2>Club Bookings Dashboard</H2>
      <H3>Calendar View</H3>
      <CardGrid>
        <Card icon="📅" title="Day View">Hourly grid for all courts. Booked slots highlighted yellow. Grey = blocked or outside hours.</Card>
        <Card icon="📆" title="Month View">High-level overview of booking density across the whole month.</Card>
        <Card icon="📅" title="Date Picker">Navigate to any specific date to view its bookings.</Card>
      </CardGrid>
      <H3>Booking List</H3>
      <Table
        headers={["Column","Description"]}
        rows={[
          ["<strong>Player</strong>","Name and profile photo of the member who booked"],
          ["<strong>Court</strong>","The court reserved (e.g. Court 1)"],
          ["<strong>Time</strong>","Date, start time, and duration (e.g. 2026-05-26 13:30, 90 min)"],
          ["<strong>Status</strong>","Blocked, Confirmed, or Pending"],
          ["<strong>Delete</strong>","Red delete icon to cancel and remove a booking"],
        ]}
      />
    </div>
  );
}

function SectionSppr() {
  return (
    <div>
      <Hero tag="Ratings" title="SPPR — ScorePal Player Rating">ScorePal's proprietary rating system that ensures rankings are fair, accurate, and dynamic — accounting for opponent strength, match importance, and consistency.</Hero>
      <H2>The SPPR Formula</H2>
      <FormulaBox><span style={{color:"#60A5FA",fontWeight:700}}>SPPR</span> = K × <span style={{color:"#60A5FA",fontWeight:700}}>Winner Overperformance Weight</span> × <span style={{color:"#60A5FA",fontWeight:700}}>Match Weight</span> × <span style={{color:"#60A5FA",fontWeight:700}}>Reliability Weight</span></FormulaBox>
      <H2>Reliability Weight</H2>
      <Table
        headers={["Reliability %","Weight","What It Means"]}
        rows={[
          ["0% – 20%","<strong>1.3</strong>","New/less active — larger changes to establish rating quickly"],
          ["21%","<strong>1.0</strong>","Standard weight"],
          ["50%","<strong>0.9</strong>","Moderately established"],
          ["70%","<strong>0.8</strong>","Established — smaller, more stable changes"],
          ["85% – 100%","<strong>0.75</strong>","Highly reliable — very stable rating"],
        ]}
      />
      <H2>Match Weight (Score Difference)</H2>
      <Table
        headers={["Score Difference","Match Weight","Impact"]}
        rows={[
          ["±21","<strong>1.2</strong>","Large win/loss — greater impact"],
          ["±12","<strong>1.1</strong>","Significant margin"],
          ["±8","<strong>1.0</strong>","Standard match"],
          ["±5","<strong>0.9</strong>","Close-ish match"],
          ["±1 to 0","<strong>0.7</strong>","Very close — smaller impact"],
        ]}
      />
      <H2>Match Type Adjustment</H2>
      <Table
        headers={["Match Type","Additional Weight"]}
        rows={[
          ["Friendly","+0.0"],
          ["League Game","+0.1"],
          ["Tournament Game","+0.1"],
          ["Semi Final","+0.3"],
          ["Final","+0.5"],
        ]}
      />
      <H2>Summary</H2>
      <CardGrid>
        <Card icon="⚖️" title="Fair">Accounts for opponent strength — beating a top player is worth more.</Card>
        <Card icon="🎯" title="Accurate">Uses actual performance and score margins, not just win/loss results.</Card>
        <Card icon="📈" title="Dynamic">Adapts as you improve, play more, and compete in higher-stakes matches.</Card>
      </CardGrid>
    </div>
  );
}

function SectionElo() {
  return (
    <div>
      <Hero tag="Ratings" title="ELO Rating">A dynamic rating that rewards unexpected wins and penalises unexpected losses — adjusting based on who you play, not just whether you win or lose.</Hero>
      <H2>How ELO Works</H2>
      <CardGrid>
        <Card icon="⬆️" title="Win vs stronger opponent">Big rating increase — you outperformed expectations.</Card>
        <Card icon="↗️" title="Win vs weaker opponent">Small rating increase — the expected result happened.</Card>
        <Card icon="↘️" title="Loss vs stronger opponent">Small rating decrease — losing to a better player was expected.</Card>
        <Card icon="⬇️" title="Loss vs weaker opponent">Big rating decrease — you underperformed against expectations.</Card>
      </CardGrid>
      <H2>Example</H2>
      <Table
        headers={["Scenario","Your Rating","Opponent Rating","Outcome"]}
        rows={[
          ["Underdog win","1200","1400","<strong>Win → big rating increase</strong>"],
          ["Underdog loss","1200","1400","<strong>Loss → small rating decrease</strong>"],
          ["Favourite win","1500","1300","<strong>Win → small rating increase</strong>"],
          ["Favourite loss","1500","1300","<strong>Loss → big rating decrease</strong>"],
        ]}
      />
      <H2>The Formula</H2>
      <FormulaBox><span style={{color:"#60A5FA",fontWeight:700}}>New Rating</span> = Current Rating + K × (Actual Score − Expected Score)</FormulaBox>
      <Table
        headers={["Variable","Meaning"]}
        rows={[
          ["<strong>K</strong>","Controls how fast ratings change"],
          ["<strong>Actual Score</strong>","1 for a win, 0 for a loss"],
          ["<strong>Expected Score</strong>","Probability of winning based on rating difference"],
        ]}
      />
      <Callout type="tip" icon="💡">To grow your ELO quickly, play competitive matches against players rated <strong>higher than you</strong>.</Callout>
    </div>
  );
}

function SectionReliability() {
  return (
    <div>
      <Hero tag="Ratings" title="Reliability Score">A percentage on your profile reflecting how up-to-date and trustworthy your rating is — based on match activity in the last 3 months.</Hero>
      <H2>What It Measures</H2>
      <CardGrid>
        <Card icon="📈" title="Score Goes Up When You…">Play matches regularly and stay active over the past 3 months.</Card>
        <Card icon="📉" title="Score Goes Down When You…">Play fewer matches or become inactive for an extended period.</Card>
      </CardGrid>
      <H2>Why Reliability Matters</H2>
      <Table
        headers={["Level","What It Means","Rating Impact"]}
        rows={[
          ["<strong>High (85%+)</strong>","Rating reflects your current ability","Smaller, more stable changes"],
          ["<strong>Medium (50%)</strong>","Moderately active — reasonably current","Moderate impact per match"],
          ["<strong>Low (20% or below)</strong>","Rating may be outdated","Larger impact to quickly re-calibrate"],
        ]}
      />
      <H2>How to Improve Your Reliability Score</H2>
      <Steps steps={[
        {title:"Play matches consistently",body:"Regular match activity within the last 3 months is the primary driver."},
        {title:"Join a league or tournament",body:"Structured competitions provide a regular flow of matches throughout the season."},
        {title:"Use matchmaking",body:"Casual matches through the Matchmaking feature also count toward recent activity."},
      ]}/>
      <Callout type="info" icon="ℹ️">Your Reliability Score is visible on your <strong>Player Profile</strong> alongside SPPR and ELO ratings.</Callout>
    </div>
  );
}

// ── Section lookup map (no JSX values — only component references) ──
const SECTION_MAP: Record<string, React.ComponentType> = {
  home:             SectionHome,
  player:           SectionPlayer,
  playerdashboard:  SectionPlayerDashboard,
  community:        SectionCommunity,
  club:             SectionClub,
  tournament:       SectionTournament,
  tournoptions:     SectionTournOptions,
  clashofteams:     SectionClashOfTeams,
  rydercup:         SectionRyderCup,
  formats:          SectionFormats,
  matchcentre:      SectionMatchCentre,
  teamsports:       SectionTeamSports,
  league:           SectionLeague,
  leagueoptions:    SectionLeagueOptions,
  ladder:           SectionLadder,
  division:         SectionDivision,
  leagueofleagues:  SectionLeagueOfLeagues,
  leagueofplayers:  SectionLeagueOfPlayers,
  clubleaderboard:  SectionClubLeaderboard,
  match:            SectionMatch,
  invite:           SectionInvite,
  referrals:        SectionReferrals,
  wallet:           SectionWallet,
  payments:         SectionPayments,
  courtbookings:    SectionCourtBookings,
  sppr:             SectionSppr,
  elo:              SectionElo,
  reliability:      SectionReliability,
};

// ── Docs UI ──────────────────────────────────────────────────────
function ScorePalDocs() {
  const [active, setActive] = useState("home");
  const [search, setSearch] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const currentLabel = ALL_ITEMS.find(i => i.id === active)?.label || "What is ScorePal?";

  const filteredNav = search
    ? [{ group: "Results", items: ALL_ITEMS.filter(i => i.label.toLowerCase().includes(search.toLowerCase())) }]
    : NAV;

  const Section = SECTION_MAP[active];

  useEffect(() => { window.scrollTo(0, 0); }, [active]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');
        :root {
          --navy:#082F8A; --navy-dark:#051d5c; --navy-mid:#0d3fad;
          --accent:#3B82F6; --accent-bright:#60A5FA;
          --border:#dde5f7;
          --sidebar-w:268px;
        }
        /* Outer flex row — sidebar + main side by side, scoped to docs section only */
        .sp-layout { display:flex; align-items:flex-start; }
        /* Sticky sidebar: stays in place while scrolling but doesn't overlap content above */
        .sp-sidebar { width:var(--sidebar-w); flex-shrink:0; background:var(--navy-dark); position:sticky; top:0; height:100vh; display:flex; flex-direction:column; overflow:hidden; transition:transform .3s ease; z-index:10; }
        .nav-scroll { flex:1; overflow-y:auto; padding:10px 0 28px; min-height:0; }
        .nav-scroll::-webkit-scrollbar { width:5px; }
        .nav-scroll::-webkit-scrollbar-track { background:rgba(255,255,255,.04); }
        .nav-scroll::-webkit-scrollbar-thumb { background:rgba(255,255,255,.25); border-radius:3px; }
        .nav-link { display:flex; align-items:center; gap:9px; padding:7px 20px; color:rgba(255,255,255,.6); text-decoration:none; font-size:13.5px; cursor:pointer; border:none; background:none; width:100%; text-align:left; border-left:2px solid transparent; transition:all .15s; }
        .nav-link:hover { color:#fff; background:rgba(255,255,255,.06); border-left-color:rgba(255,255,255,.2); }
        .nav-link.active { color:var(--accent-bright); background:rgba(59,130,246,.13); border-left-color:var(--accent-bright); font-weight:500; }
        /* Main takes remaining width */
        .sp-main { flex:1; min-width:0; }
        .sp-content { max-width:820px; margin:0 auto; padding:44px 40px 80px; font-size:15px; line-height:1.75; font-family:'DM Sans',sans-serif; }
        .sp-menu-btn { display:none; background:none; border:none; cursor:pointer; font-size:20px; color:var(--navy); }
        @media(max-width:768px){
          .sp-layout { display:block; }
          .sp-sidebar { position:fixed; top:0; left:0; width:var(--sidebar-w); height:100vh; transform:translateX(-100%); z-index:200; }
          .sp-sidebar.open { transform:translateX(0); }
          .sp-main { margin-left:0; }
          .sp-content { padding:28px 18px 60px; }
          .sp-menu-btn { display:block; }
        }
      `}</style>

      {/* ── Docs layout: sidebar + main in a flex row ── */}
      <div className="sp-layout">

      {/* Sidebar */}
      <div className={`sp-sidebar${sidebarOpen ? " open" : ""}`}>
        <div style={{ padding:"26px 22px 18px", borderBottom:"1px solid rgba(255,255,255,.08)" }}>
          <div style={{ fontFamily:"'Bebas Neue',cursive", fontWeight:400, fontSize:24, color:"#fff", letterSpacing:2 }}>⚡ ScorePal</div>
          <div style={{ fontSize:10, color:"rgba(255,255,255,.4)", letterSpacing:".1em", textTransform:"uppercase", marginTop:3 }}>Documentation</div>
        </div>
        <div style={{ padding:"12px 14px", borderBottom:"1px solid rgba(255,255,255,.06)" }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search docs…"
            style={{
              width:"100%", background:"rgba(255,255,255,.07)", border:"1px solid rgba(255,255,255,.1)",
              borderRadius:8, padding:"7px 12px", color:"#fff", fontFamily:"'DM Sans',sans-serif",
              fontSize:13, outline:"none",
            }}
          />
        </div>
        <div className="nav-scroll">
          {filteredNav.map(group => (
            <div key={group.group} style={{ marginBottom:2 }}>
              {!search && (
                <div style={{ fontSize:10, fontWeight:600, letterSpacing:".1em", textTransform:"uppercase", color:"rgba(255,255,255,.28)", padding:"12px 20px 4px" }}>
                  {group.group}
                </div>
              )}
              {group.items.map(item => (
                <button
                  key={item.id}
                  className={`nav-link${active === item.id ? " active" : ""}`}
                  onClick={() => { setActive(item.id); setSidebarOpen(false); }}
                >
                  <span>{item.icon}</span> {item.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Main */}
      <div className="sp-main">
        {/* Topbar */}
        <div style={{
          position:"sticky", top:0, zIndex:50, background:"rgba(248,250,255,.93)",
          backdropFilter:"blur(12px)", borderBottom:"1px solid var(--border)",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"0 40px", height:54,
        }}>
          <div style={{ fontSize:13, color:"#64748b", display:"flex", alignItems:"center", gap:6 }}>
            ScorePal Docs ›{" "}
            <span style={{ color:"var(--navy)", fontWeight:500 }}>{currentLabel}</span>
          </div>
          <div style={{ display:"flex", gap:12, alignItems:"center" }}>
            <a href="https://scorepal.co" target="_blank" rel="noreferrer" style={{ fontSize:13, color:"#64748b", textDecoration:"none", padding:"5px 12px", borderRadius:6 }}>Website</a>
            <a href="https://scorepal.co/signup/player/" target="_blank" rel="noreferrer" style={{ fontSize:13, background:"var(--navy)", color:"#fff", textDecoration:"none", padding:"5px 12px", borderRadius:6, fontWeight:500 }}>Sign Up Free</a>
            <button className="sp-menu-btn" onClick={() => setSidebarOpen(o => !o)}>☰</button>
          </div>
        </div>

        {/* Content */}
        <div className="sp-content">
          {Section ? <Section /> : <P>Section not found.</P>}
        </div>
      </div>

      </div>{/* end sp-layout */}
    </>
  );
}

// ── Route ────────────────────────────────────────────────────────
export const Route = createFileRoute('/documentation')({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <div className="min-h-screen bg-[#EDEEF4]">
      <JsonLdBreadcrumbList
        routeId={Route.id}
        crumbs={[{ name: "Documentation", item: "https://scorepal.co/documentation/" }]}
      />
      <title>Documentation | ScorePal</title>
      <meta name="description" content="Documentation for sports tournaments and leagues with real-time updates, automated results, and seamless match tracking for clubs." />
      <link rel="canonical" href={`${config.publicUrl}/documentation/`} />

      <div style={{ background: "#051d5c" }}>
        <LandingNav />
      </div>

      {/* Page header strip — negative marginTop pulls it up behind the transparent nav */}
      <div style={{
        background: "linear-gradient(135deg, #051d5c 0%, #0d3fad 100%)",
        padding: "28px 60px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", right: -40, top: "50%", transform: "translateY(-50%)",
          width: 200, height: 200,
          background: "rgba(59,130,246,.07)", borderRadius: "50%",
        }}/>
        <div>
          <div style={{
            fontSize: 10, fontWeight: 600, letterSpacing: ".12em",
            textTransform: "uppercase", color: "rgba(255,255,255,.5)",
            marginBottom: 4, fontFamily: "'DM Sans', sans-serif",
          }}>Help Center</div>
          <h1 style={{
            fontFamily: "'Bebas Neue', cursive",
            fontSize: "clamp(22px, 3vw, 32px)",
            fontWeight: 400, letterSpacing: 2,
            color: "#fff", lineHeight: 1, margin: 0,
          }}>ScorePal Documentation</h1>
        </div>
        <p style={{
          color: "rgba(255,255,255,.6)",
          fontSize: 13.5, maxWidth: 380, lineHeight: 1.6, margin: 0,
          fontFamily: "'DM Sans', sans-serif", textAlign: "right",
        }}>
          Step-by-step guides for leagues, tournaments, clubs, and ratings.
        </p>
      </div>

      <ScorePalDocs />

      <FooterSection />
    </div>
  );
}
